import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UtensilsCrossed, BedDouble } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/layout/WhatsAppButton";
import WhatsAppPopup from "@/components/layout/WhatsAppPopup";
import SEO from "@/components/SEO";
import DynamicContentBlocks from "@/components/DynamicContentBlocks";
import ReviewsMarquee from "@/components/ReviewsMarquee";
import PhotoCarousel from "@/components/PhotoCarousel";
import ScidooReviewsWidget from "@/components/home/ScidooReviewsWidget";
import UpcomingEvents from "@/components/home/UpcomingEvents";
import AreasBento from "@/components/home/AreasBento";
import FAQSection from "@/components/home/FAQSection";
import NewsSection from "@/components/home/NewsSection";
import api, { eventsApi, normalizeImageUrl } from "@/lib/api";
import { sizedImage, heroWidth } from "@/lib/imageUtils";

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  
  const lang = i18n.language;
  
  // Dynamic sections from CMS or fallback with stock images
  const defaultSectionImages = {
    "/negozio": "",
    "/esperienze": "",
    "/didattica": "",
    "/ristorante-agricolo": "",
    "/soggiorni": "",
    "/chi-siamo": ""
  };
  
  const sections = content?.homepage_sections?.items?.map(item => ({
    href: item.href,
    label: lang === 'it' ? item.label_it : (item.label_en || item.label_it),
    image: normalizeImageUrl(item.image) || defaultSectionImages[item.href],
    positionY: item.image_position_y
  })) || [
    { href: "/negozio", label: t('common.shop'), image: defaultSectionImages["/negozio"] },
    { href: "/esperienze", label: t('common.events'), image: defaultSectionImages["/esperienze"] },
    { href: "/didattica", label: t('common.education'), image: defaultSectionImages["/didattica"] },
    { href: "/ristorante-agricolo", label: t('common.farmhouse'), image: defaultSectionImages["/ristorante-agricolo"] },
    { href: "/soggiorni", label: t('common.stays'), image: defaultSectionImages["/soggiorni"] },
    { href: "/chi-siamo", label: t('common.about'), image: defaultSectionImages["/chi-siamo"] },
  ];
  if (content && !sections.some((s) => s.href === "/azienda-agricola")) {
    sections.push({ href: "/azienda-agricola", label: t('common.farm'), image: normalizeImageUrl(content.farm_production?.hero_image) });
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsData, contentData] = await Promise.all([
          eventsApi.getAll(),
          api.get("/content").then(r => r.data).catch(() => null)
        ]);
        setEvents(eventsData);
        setContent(contentData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Parse YYYY-MM-DD as local date (avoids UTC timezone shift bug)
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // Filter out past events
  const futureEvents = events.filter(event => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (event.event_type === 'appointment') return true;
    if (event.fixed_date) return parseLocalDate(event.fixed_date) >= today;
    const dates = (event.event_dates_times || []).map(dt => dt.date)
      .concat(event.event_dates || []);
    if (dates.length > 0) return dates.some(d => parseLocalDate(d) >= today);
    return true;
  });

  // Get hero content from CMS
  const hero = content?.homepage_hero || {};
  const family = content?.homepage_family || {};

  return (
    <div className="min-h-screen flex flex-col" data-testid="home-page">
      <SEO 
        title={null}
        description="Agriturismo Sadira a Calusco d'Adda (Bergamo): camere in agriturismo, ristorante con cucina bergamasca a km zero, vendita prodotti agricoli, fattoria didattica per bambini, esperienze e laboratori nel weekend. Prenota online il tuo soggiorno o esperienza."
        keywords="agriturismo bergamo, agriturismo calusco d'adda, agriturismo con ristorante bergamo, ristorante km zero bergamo, fattoria didattica bergamo, camere agriturismo bergamo, weekend in agriturismo bergamo, agriturismo con piscina bergamo, esperienze in fattoria, prodotti agricoli bergamo, agriturismo famiglia bergamo, dove mangiare bergamo domenica"
        url="/"
      />
      <Navbar />
      
      {/* Hero Section with Video Background */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Video Background - YouTube or MP4 */}
        {(hero.youtube_url || (hero.video_url && (hero.video_url.includes('youtube.com') || hero.video_url.includes('youtu.be')))) ? (
          // YouTube embed as background
          (() => {
            const ytUrl = hero.youtube_url || hero.video_url;
            const videoId = ytUrl.includes('youtu.be/') 
              ? ytUrl.split('youtu.be/')[1].split('?')[0]
              : ytUrl.includes('watch?v=')
                ? ytUrl.split('watch?v=')[1].split('&')[0]
                : ytUrl.split('/embed/')[1]?.split('?')[0];
            return (
              <div className="absolute inset-0 w-full h-full overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&playlist=${videoId}&playsinline=1`}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full min-h-full"
                  style={{ border: 'none', pointerEvents: 'none' }}
                  title="Background Video"
                />
              </div>
            );
          })()
        ) : hero.video_url ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster={sizedImage(normalizeImageUrl(hero.image), heroWidth())}
          >
            <source src={normalizeImageUrl(hero.video_url)} type="video/mp4" />
          </video>
        ) : (
          <div 
            className="absolute inset-0 hero-parallax"
            style={{ 
              backgroundImage: `url('${sizedImage(normalizeImageUrl(hero.image), heroWidth())}')`
            }}
          />
        )}
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto animate-fade-in-up">
          {hero.tagline_image && !hero.tagline_image.includes('agriturismosadira.it') && (
            <img 
              src={normalizeImageUrl(hero.tagline_image)}
              alt={t('hero.tagline')}
              className="h-8 sm:h-12 md:h-16 mx-auto mb-4 sm:mb-6"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <h1 
            className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-tight px-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {(lang === 'it' ? hero.title_it : (hero.title_en || hero.title_it)) || t('hero.title')}
          </h1>
          <p className="text-base sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto px-2">
            {(lang === 'it' ? hero.subtitle_it : (hero.subtitle_en || hero.subtitle_it)) || t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 justify-center px-4">
            <Link to="/esperienze" data-testid="hero-cta-events">
              <Button className="btn-primary bg-[#dbe3c8] text-[#2C2C2C] rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-medium hover:bg-[#c9d4b0] w-full sm:w-auto">
                {t('hero.discoverEvents')}
                <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
              </Button>
            </Link>
            <Link to="/ristorante-agricolo" data-testid="hero-cta-restaurant">
              <Button className="bg-[#ccbcae] text-[#2C2C2C] rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-medium hover:bg-[#b9a899] w-full sm:w-auto">
                <UtensilsCrossed className="mr-2 w-4 sm:w-5 h-4 sm:h-5" />
                {t('nav.bookTable')}
              </Button>
            </Link>
            <a 
              href="https://www.scidoo.com/preventivov2/?cod=1649&IDsettore_specifico=574&settore=1&settore_unico=1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-rooms"
              className="w-full sm:w-auto"
            >
              <Button className="bg-[#573613] text-white rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-medium hover:bg-[#3d250d] w-full">
                <BedDouble className="mr-2 w-4 sm:w-5 h-4 sm:h-5" />
                {t('nav.bookRooms')}
              </Button>
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Dynamic Content Blocks - Top (after hero) */}
      <DynamicContentBlocks page="homepage" position="top" />

      {/* Upcoming Events */}
      <UpcomingEvents events={futureEvents} loading={loading} />

      {/* Areas */}
      <AreasBento sections={sections} />

      {/* Reviews Marquee Section */}
      <ReviewsMarquee />

      {/* Family Section */}
      <section data-reveal className="py-16 md:py-24 bg-[#dbe3c8]/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="relative rounded-3xl overflow-hidden shadow-xl">
            <div className="h-[400px] md:h-[500px] overflow-hidden">
              <div className="h-[115%] -mt-[7%]" data-parallax="0.06">
                <img loading="lazy" decoding="async" 
                  src={sizedImage(normalizeImageUrl(family.image), 1600)}
                  alt="Famiglia Sadira"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: `50% ${family.image_position_y ?? 50}%` }}
                />
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {(lang === 'it' ? family.title_it : (family.title_en || family.title_it)) || t('home.ourFamily')}
                </h2>
                <p className="text-lg text-white/90 max-w-xl">
                  {(lang === 'it' ? family.subtitle_it : (family.subtitle_en || family.subtitle_it)) || t('home.ourFamilySubtitle')}
                </p>
              </div>
              <Link to="/chi-siamo" data-testid="family-discover-btn">
                <Button className="bg-white text-[#7e823a] hover:bg-white/90 rounded-full px-8 py-3 text-lg font-medium shadow-lg">
                  {t('common.discover')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Content Blocks - Middle */}
      <DynamicContentBlocks page="homepage" position="middle" />

      {/* News Section (linked to Instagram, SEO-friendly) */}
      <NewsSection />

      {/* FAQ Section (with FAQPage JSON-LD for SEO) */}
      <FAQSection />

      {/* WhatsApp & Telegram CTA */}
      <section data-reveal className="py-12 bg-[#dbe3c8]/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h3 
            className="text-2xl md:text-3xl font-semibold text-[#7e823a] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t('home.stayUpdated')}
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="https://whatsapp.com/channel/0029VaCbc6K6xCSVAFxKnf3D"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full text-base font-semibold hover:bg-[#20BD5A] transition-colors shadow-md"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t('common.whatsappChannel')}
            </a>
            <a 
              href="https://t.me/agriturismosadira"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0088cc] text-white px-6 py-3 rounded-full text-base font-semibold hover:bg-[#0077b5] transition-colors shadow-md"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              {t('common.telegramChannel')}
            </a>
          </div>
        </div>
      </section>

      {/* Dynamic Content Blocks - Bottom */}
      <DynamicContentBlocks page="homepage" position="bottom" />

      {/* Photo Gallery Carousel */}
      <PhotoCarousel page="homepage" />

      {/* Scidoo Reviews Widget */}
      <ScidooReviewsWidget />

      <Footer />
      <WhatsAppButton />
      <WhatsAppPopup />
    </div>
  );
};

export default HomePage;
